import React from 'react'
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  VStack,
  HStack,
  Icon,
  Badge,
  Button,
  List,
} from '@chakra-ui/react'
import { FaCheck, FaTimes, FaCrown } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'
import { trackCTAClick } from '../services/analytics'
import { LightMode } from './ui/color-mode'

const PLANES = [
  {
    tier: 'GRATIS',
    nombre: 'Gratis',
    resumen: 'Para empezar y darte a conocer',
    destacado: false,
    precioNota: 'Incluido en el catálogo',
    incluye: [
      'Perfil público y comunidad',
      'Tu Web (micrositio)',
      'Calendario y eventos (limitados)',
      'Acuerdos y contactos básicos',
      'Archivo musical limitado',
    ],
    excluye: ['Mis músicos, formaciones, actuaciones y ensayos'],
  },
  {
    tier: 'ESTANDAR',
    nombre: 'Estándar',
    resumen: 'Para bandas en marcha',
    destacado: false,
    precioNota: 'Cuotas según plan',
    incluye: [
      'Todo lo del plan gratis',
      'Mis músicos y formaciones',
      'Actuaciones y ensayos',
      'Objetivos y encuestas',
      'Permisos de colaboradores',
    ],
    excluye: ['Dominio propio (disponible como extra)'],
  },
  {
    tier: 'INTERMEDIO',
    nombre: 'Intermedio',
    resumen: 'Más almacenamiento y capacidad',
    destacado: false,
    precioNota: 'Cuotas según plan',
    incluye: [
      'Todo lo del plan estándar',
      'Mayor archivo musical',
      'Más capacidad de IA',
    ],
    excluye: ['Dominio propio (disponible como extra)'],
  },
  {
    tier: 'AVANZADO',
    nombre: 'Avanzado',
    resumen: 'Sin límites para bandas exigentes',
    destacado: true,
    precioNota: 'Gratis para bandas validadas en el lanzamiento',
    incluye: [
      'Todo lo del plan intermedio',
      'Dominio propio incluido',
      'Archivo musical e IA ampliados',
      'Máximo almacenamiento',
    ],
    excluye: [],
  },
]

const PlanCard = ({ plan, onCta }) => {
  const borderColor = plan.destacado ? 'purple.400' : 'gray.200'
  return (
    <VStack
      align="stretch"
      gap={5}
      p={{ base: 6, md: 8 }}
      bg="white"
      rounded="3xl"
      borderWidth="2px"
      borderColor={borderColor}
      boxShadow={plan.destacado ? 'xl' : 'md'}
      position="relative"
      transform={{ md: plan.destacado ? 'scale(1.03)' : 'none' }}
    >
      {plan.destacado && (
        <Badge
          position="absolute"
          top={-3}
          left="50%"
          transform="translateX(-50%)"
          colorPalette="purple"
          rounded="full"
          px={3}
          py={1}
          fontSize="0.7rem"
        >
          <HStack gap={1}>
            <Icon as={FaCrown} />
            <Text>Recomendado</Text>
          </HStack>
        </Badge>
      )}
      <VStack align="start" gap={1}>
        <Heading size="md" color="gray.800">
          {plan.nombre}
        </Heading>
        <Text fontSize="sm" color="gray.500">
          {plan.resumen}
        </Text>
      </VStack>

      <Box>
        <Text fontSize="md" fontWeight="800" color="purple.600">
          {plan.destacado ? 'Gratis en el lanzamiento' : plan.precioNota}
        </Text>
        {plan.destacado ? (
          <Text fontSize="xs" color="gray.500" fontWeight="600">
            Para bandas validadas, por tiempo limitado
          </Text>
        ) : (
          <Text fontSize="xs" color="gray.500">
            Sin precio público inventado: el detalle está en Mi suscripción
          </Text>
        )}
      </Box>

      <List.Root gap={2} flex="1">
        {plan.incluye.map((f) => (
          <List.Item key={f} fontSize="sm" color="gray.700">
            <List.Indicator as={FaCheck} color="green.500" />
            {f}
          </List.Item>
        ))}
        {plan.excluye.map((f) => (
          <List.Item key={f} fontSize="sm" color="gray.400">
            <List.Indicator as={FaTimes} color="gray.300" />
            {f}
          </List.Item>
        ))}
      </List.Root>

      <Button colorPalette="purple" variant={plan.destacado ? 'solid' : 'outline'} rounded="full" onClick={() => onCta(plan.tier)}>
        Empezar
      </Button>
    </VStack>
  )
}

const Planes = () => {
  const navigate = useNavigate()

  const handleCta = (tier) => {
    trackCTAClick(`plan_${tier.toLowerCase()}`, 'planes')
    navigate('/registro?tipo=banda')
  }

  return (
    <LightMode>
      <Box id="planes" data-landing="planes" bg="gray.50" py={{ base: 16, md: 24 }} color="gray.800">
        <Container maxW="7xl">
        <VStack gap={{ base: 10, md: 14 }}>
          <VStack gap={4} textAlign="center">
            <Heading
              as="h2"
              fontSize={{ base: '3xl', md: '4xl', lg: '5xl' }}
              fontWeight="800"
              color="gray.800"
            >
              Planes para cada banda
            </Heading>
            <Text fontSize={{ base: 'lg', md: 'xl' }} color="gray.600" maxW="2xl">
              Cuatro tiers reales. Las hermandades usan un plan gratuito propio.
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap={{ base: 6, md: 6 }} w="full">
            {PLANES.map((plan) => (
              <PlanCard key={plan.tier} plan={plan} onCta={handleCta} />
            ))}
          </SimpleGrid>

          <Text fontSize="sm" color="gray.500" textAlign="center" maxW="3xl">
            El plan Avanzado está gratis durante el lanzamiento para las bandas validadas.
            El resto del catálogo (Gratis, Estándar, Intermedio) está activo; las cuotas
            se ven al elegir plan en la cuenta.
          </Text>
        </VStack>
        </Container>
      </Box>
    </LightMode>
  )
}

export default Planes
